import * as THREE from 'three'
import type { MapManifest, MineInstance, SlotState } from '@/gameplay/types'

/** Доля tileSize для меша — микро-шов между клетками без разрыва решётки. */
const TILE_FACE_SCALE = 0.96

const MAT = {
  concrete: new THREE.MeshStandardMaterial({ color: 0x6e7378, roughness: 0.9, metalness: 0.05 }),
  metal: new THREE.MeshStandardMaterial({ color: 0x4e555c, roughness: 0.55, metalness: 0.4 }),
  rust: new THREE.MeshStandardMaterial({ color: 0x8b5a3c, roughness: 0.85, metalness: 0.15 }),
  hazard: new THREE.MeshStandardMaterial({ color: 0xc4a35a, roughness: 0.7, metalness: 0.2 }),
  exit: new THREE.MeshStandardMaterial({
    color: 0xc4a35a,
    emissive: 0xc4a35a,
    emissiveIntensity: 0.35,
    transparent: true,
    opacity: 0.55,
  }),
  slotUnknown: new THREE.MeshStandardMaterial({ color: 0x3a3f44, roughness: 0.8, metalness: 0.2 }),
  slotSafe: new THREE.MeshStandardMaterial({ color: 0x6b9e78, roughness: 0.75, metalness: 0.1 }),
  slotDefused: new THREE.MeshStandardMaterial({
    color: 0x5a8aaa,
    emissive: 0x2a5060,
    emissiveIntensity: 0.2,
    roughness: 0.65,
    metalness: 0.25,
  }),
  slotBoom: new THREE.MeshStandardMaterial({
    color: 0xc45c4a,
    emissive: 0xc45c4a,
    emissiveIntensity: 0.5,
  }),
  slotHover: new THREE.MeshStandardMaterial({
    color: 0x7ec4a0,
    emissive: 0x7ec4a0,
    emissiveIntensity: 0.25,
  }),
  defuseMark: new THREE.MeshStandardMaterial({
    color: 0x5a8aaa,
    emissive: 0x3a7080,
    emissiveIntensity: 0.4,
    roughness: 0.5,
    metalness: 0.3,
  }),
  channelRing: new THREE.MeshBasicMaterial({
    color: 0xc4a35a,
    transparent: true,
    opacity: 0.55,
    side: THREE.DoubleSide,
  }),
  /** Тёмный кант на inset-тайле — читаемый шов с высоты глаз. */
  slotEdge: new THREE.LineBasicMaterial({ color: 0x2a2e33 }),
}

const DIGIT_COLORS = [
  '#8A9098',
  '#5B8FD4',
  '#5FAF6E',
  '#D45B5B',
  '#7B5BB8',
  '#B85B3A',
  '#3AA0A8',
  '#2A2E33',
  '#8A9098',
]

export class MapRuntime {
  readonly root = new THREE.Group()
  readonly slotMeshes = new Map<string, THREE.Mesh>()
  /** Decal на поверхности тайла (не billboard в воздухе). */
  private numberDecals = new Map<string, THREE.Mesh>()
  private defuseMarkers = new Map<string, THREE.Mesh>()
  private channelCue: THREE.Mesh | null = null
  private hoverId: string | null = null
  /** Портал + арка — прячем целиком в режиме Clear */
  private exitGroup: THREE.Group | null = null

  constructor(readonly manifest: MapManifest) {
    this.build()
  }

  private build(): void {
    // Пол
    for (const p of this.manifest.platforms) {
      const geo = new THREE.BoxGeometry(p.w, 0.2, p.d)
      const mesh = new THREE.Mesh(geo, MAT.concrete)
      mesh.position.set(p.x, (p.y ?? 0) - 0.1, p.z)
      mesh.receiveShadow = true
      this.root.add(mesh)

      // Hazard-полосы
      const stripe = new THREE.Mesh(
        new THREE.BoxGeometry(Math.min(4, p.w * 0.3), 0.02, 0.35),
        MAT.hazard,
      )
      stripe.position.set(p.x, 0.01, p.z - p.d * 0.25)
      this.root.add(stripe)
    }

    for (const w of this.manifest.walls) {
      const h = w.h ?? 3
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(w.w, h, w.d), MAT.metal)
      mesh.position.set(w.x, h / 2, w.z)
      mesh.castShadow = true
      this.root.add(mesh)
    }

    // Solid props (коробки) — меш; коллизия в FpsController
    for (const prop of this.manifest.props ?? []) {
      const h = prop.h ?? 1.2
      const crate = new THREE.Mesh(new THREE.BoxGeometry(prop.w, h, prop.d), MAT.rust)
      crate.position.set(prop.x, h / 2, prop.z)
      crate.castShadow = true
      crate.receiveShadow = true
      this.root.add(crate)
    }

    // Exit portal (диск + арка)
    const exitGroup = new THREE.Group()
    const exit = new THREE.Mesh(new THREE.CylinderGeometry(1.6, 1.6, 0.15, 24), MAT.exit)
    exit.position.set(
      this.manifest.exitZone.center.x,
      0.08,
      this.manifest.exitZone.center.z,
    )
    exitGroup.add(exit)

    const arch = new THREE.Mesh(new THREE.BoxGeometry(3.2, 3.2, 0.4), MAT.rust)
    arch.position.set(
      this.manifest.exitZone.center.x,
      1.6,
      this.manifest.exitZone.center.z + 0.8,
    )
    exitGroup.add(arch)
    this.exitGroup = exitGroup
    this.root.add(exitGroup)

    // Квадратные тайлы: шаг = tileSize, face чуть меньше — шов без «дыры»
    const tile = this.manifest.tileSize
    const face = tile * TILE_FACE_SCALE
    for (const slot of this.manifest.slots) {
      const geo = new THREE.BoxGeometry(face, 0.1, face)
      const mesh = new THREE.Mesh(geo, MAT.slotUnknown)
      mesh.position.set(slot.position.x, slot.position.y, slot.position.z)
      mesh.userData.slotId = slot.id
      mesh.userData.state = 'unknown' as SlotState
      // Лёгкий bevel-контур (EdgesGeometry) — сетка читается во flush
      const edge = new THREE.LineSegments(new THREE.EdgesGeometry(geo), MAT.slotEdge)
      mesh.add(edge)
      this.slotMeshes.set(slot.id, mesh)
      this.root.add(mesh)
    }

    // Общий channel cue (центр слота — без спойлера mine/empty)
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.25, 0.4, 24), MAT.channelRing)
    ring.rotation.x = -Math.PI / 2
    ring.visible = false
    this.channelCue = ring
    this.root.add(ring)
  }

  setExitVisible(visible: boolean): void {
    if (this.exitGroup) this.exitGroup.visible = visible
  }

  setHover(slotId: string | null): void {
    if (this.hoverId === slotId) return
    if (this.hoverId) {
      const prev = this.slotMeshes.get(this.hoverId)
      if (prev) prev.material = this.materialForState(prev.userData.state as SlotState)
    }
    this.hoverId = slotId
    if (slotId) {
      const mesh = this.slotMeshes.get(slotId)
      if (
        mesh &&
        mesh.userData.state !== 'safeRevealed' &&
        mesh.userData.state !== 'mineExploded' &&
        mesh.userData.state !== 'defused'
      ) {
        mesh.material = MAT.slotHover
      }
    }
  }

  /** Baked adjacent-count — decal на верхней грани тайла. */
  bakeAdjacent(slotId: string, count: number): void {
    const mesh = this.slotMeshes.get(slotId)
    if (!mesh) return
    this.ensureNumberDecal(slotId, count, mesh.position, this.manifest.tileSize)
  }

  syncSlot(slotId: string, state: SlotState, adjacentCount?: number): void {
    const mesh = this.slotMeshes.get(slotId)
    if (!mesh) return
    mesh.userData.state = state
    if (this.hoverId === slotId && state === 'unknown') {
      mesh.material = MAT.slotHover
    } else {
      mesh.material = this.materialForState(state)
    }

    if (state === 'safeRevealed' && adjacentCount !== undefined) {
      this.bakeAdjacent(slotId, adjacentCount)
    }

    if (state === 'defused') {
      // Маркер без offset — offset ставит syncDefusedMine
      this.ensureDefuseMarker(slotId, mesh.position.x, mesh.position.z)
    }
  }

  /** Показать обезвреженную мину в точке offset. */
  syncDefusedMine(mine: MineInstance, slotPos: { x: number; z: number }): void {
    const x = slotPos.x + mine.offset.x
    const z = slotPos.z + mine.offset.z
    this.ensureDefuseMarker(mine.slotId, x, z)
  }

  /** Channel cue на центре слота (без различия mine/empty). */
  setChannelCue(slotId: string | null, t: number): void {
    if (!this.channelCue) return
    if (!slotId) {
      this.channelCue.visible = false
      return
    }
    const mesh = this.slotMeshes.get(slotId)
    if (!mesh) {
      this.channelCue.visible = false
      return
    }
    this.channelCue.visible = true
    this.channelCue.position.set(mesh.position.x, mesh.position.y + 0.12, mesh.position.z)
    const s = 0.85 + t * 0.35
    this.channelCue.scale.set(s, s, s)
    MAT.channelRing.opacity = 0.35 + t * 0.45
  }

  private ensureDefuseMarker(slotId: string, x: number, z: number): void {
    let marker = this.defuseMarkers.get(slotId)
    if (!marker) {
      marker = new THREE.Mesh(new THREE.SphereGeometry(0.12, 10, 8), MAT.defuseMark)
      this.defuseMarkers.set(slotId, marker)
      this.root.add(marker)
    }
    marker.position.set(x, 0.22, z)
    marker.visible = true
  }

  private materialForState(state: SlotState): THREE.Material {
    switch (state) {
      case 'safeRevealed':
        return MAT.slotSafe
      case 'defused':
        return MAT.slotDefused
      case 'mineExploded':
        return MAT.slotBoom
      default:
        return MAT.slotUnknown
    }
  }

  /**
   * Цифра как plane-decal на полу тайла (не Sprite billboard).
   * count=0 — без глифа (только материал тайла).
   */
  private ensureNumberDecal(
    slotId: string,
    count: number,
    pos: THREE.Vector3,
    tileSize: number,
  ): void {
    if (count <= 0) {
      const existing = this.numberDecals.get(slotId)
      if (existing) existing.visible = false
      return
    }

    let decal = this.numberDecals.get(slotId)
    if (!decal) {
      const canvas = document.createElement('canvas')
      canvas.width = 128
      canvas.height = 128
      const ctx = canvas.getContext('2d')!
      ctx.clearRect(0, 0, 128, 128)
      ctx.fillStyle = 'rgba(26,29,33,0.78)'
      ctx.beginPath()
      // Скруглённая плашка
      const r = 12
      ctx.moveTo(16 + r, 16)
      ctx.arcTo(112, 16, 112, 112, r)
      ctx.arcTo(112, 112, 16, 112, r)
      ctx.arcTo(16, 112, 16, 16, r)
      ctx.arcTo(16, 16, 112, 16, r)
      ctx.closePath()
      ctx.fill()
      ctx.strokeStyle = '#4A5560'
      ctx.lineWidth = 3
      ctx.stroke()
      ctx.fillStyle = DIGIT_COLORS[count] ?? '#E8ECEF'
      ctx.font = 'bold 72px monospace'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(String(count), 64, 70)

      const tex = new THREE.CanvasTexture(canvas)
      tex.colorSpace = THREE.SRGBColorSpace
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
      })
      const size = tileSize * 0.52
      decal = new THREE.Mesh(new THREE.PlaneGeometry(size, size), mat)
      // Лежит на верхней грани тайла (y ≈ 0.06), не в воздухе
      decal.rotation.x = -Math.PI / 2
      this.numberDecals.set(slotId, decal)
      this.root.add(decal)
    }

    // Чуть выше верхней грани бокса тайла (0.05) — без z-fight
    decal.position.set(pos.x, pos.y + 0.056, pos.z)
    decal.visible = true
  }

  raycastSlot(raycaster: THREE.Raycaster, maxDist = 4): string | null {
    const meshes = [...this.slotMeshes.values()]
    const hits = raycaster.intersectObjects(meshes, false)
    const hit = hits.find((h) => h.distance <= maxDist)
    return hit ? ((hit.object.userData.slotId as string) ?? null) : null
  }

  dispose(): void {
    // Shared MAT не dispose — только геометрии и decal-текстуры
    for (const decal of this.numberDecals.values()) {
      const mat = decal.material
      if (mat instanceof THREE.MeshBasicMaterial) {
        mat.map?.dispose()
        mat.dispose()
      }
      decal.geometry.dispose()
    }
    this.numberDecals.clear()
    this.root.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        // Decals уже освобождены выше
        if (this.numberDecals.has(obj.userData.slotId as string)) return
        obj.geometry.dispose()
      }
      if (obj instanceof THREE.LineSegments) {
        obj.geometry.dispose()
      }
    })
  }
}
